import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Text,
  VStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  IconButton,
  useTheme,
  Textarea,
  Button,
  HStack,
  useToast,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon, EditIcon } from '@chakra-ui/icons';
import { updateCardDescription } from '../../ProjectBoardServices/BoardModalAPI/RightModalAPI/InfoTabAPI/DueDateAPI';

// Tab Components
import CommentsTab from './ModalTabsComponents/LeftModalComponents/CommentsTab'
import HistoryTab from './ModalTabsComponents/LeftModalComponents/HistoryTab';
import AttachmentsTab from './ModalTabsComponents/LeftModalComponents/AttachmentsTab';
import DeliveryTab from './ModalTabsComponents/LeftModalComponents/DeliveryTab';
import WorkLog from './ModalTabsComponents/LeftModalComponents/WorkLog';
import RelatedDesigns from './ModalTabsComponents/LeftModalComponents/RelatedDesigns'
import ProjectCommentsTab from './ModalTabsComponents/LeftModalComponents/ProjectCommentsTab';
import Labels from './ModalTabsComponents/LeftModalComponents/Labels';

const ScrollableTabList = ({ children, ...props }) => {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const tabListRef = useRef(null);
  const theme = useTheme();

  const checkScrollability = () => {
    if (tabListRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabListRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  useEffect(() => {
    checkScrollability();
    window.addEventListener('resize', checkScrollability);
    return () => window.removeEventListener('resize', checkScrollability);
  }, []);

  const scrollTabs = (direction) => {
    if (tabListRef.current) {
      const scrollAmount = 200;
      const newScrollLeft = tabListRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      
      tabListRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
      
      setTimeout(checkScrollability, 300);
    }
  };

  return (
    <Box position="relative" width="100%">
      {canScrollLeft && (
        <IconButton
          icon={<ChevronLeftIcon />}
          size="sm"
          position="absolute"
          left="-2"
          top="50%"
          transform="translateY(-50%)"
          zIndex={10}
          bg="white"
          border="1px solid #DFE1E6"
          borderRadius="full"
          boxShadow="0 2px 4px rgba(0,0,0,0.1)"
          _hover={{ bg: 'gray.50' }}
          onClick={() => scrollTabs('left')}
          aria-label="Scroll tabs left"
        />
      )}

      <Box
        ref={tabListRef}
        overflowX="auto"
        overflowY="hidden"
        css={{
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          '-ms-overflow-style': 'none',
          'scrollbar-width': 'none',
        }}
        onScroll={checkScrollability}
        position="relative"
        mx={canScrollLeft || canScrollRight ? 6 : 0}
      >
        <TabList
          borderBottom="1px solid #DFE1E6"
          display="flex"
          flexWrap="nowrap"
          minWidth="max-content"
          {...props}
        >
          {children}
        </TabList>

        {canScrollLeft && (
          <Box
            position="absolute"
            left={0}
            top={0}
            bottom={0}
            width="20px"
            background="linear-gradient(to right, rgba(255,255,255,0.9), transparent)"
            pointerEvents="none"
            zIndex={5}
          />
        )}
        {canScrollRight && (
          <Box
            position="absolute"
            right={0}
            top={0}
            bottom={0}
            width="20px"
            background="linear-gradient(to left, rgba(255,255,255,0.9), transparent)"
            pointerEvents="none"
            zIndex={5}
          />
        )}
      </Box>

      {canScrollRight && (
        <IconButton
          icon={<ChevronRightIcon />}
          size="sm"
          position="absolute"
          right="-2"
          top="50%"
          transform="translateY(-50%)"
          zIndex={10}
          bg="white"
          border="1px solid #DFE1E6"
          borderRadius="full"
          boxShadow="0 2px 4px rgba(0,0,0,0.1)"
          _hover={{ bg: 'gray.50' }}
          onClick={() => scrollTabs('right')}
          aria-label="Scroll tabs right"
        />
      )}
    </Box>
  );
};

const ModalLeftSection = ({ task }) => {
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [description, setDescription] = useState(task?.description || '');
  const [isSaving, setIsSaving] = useState(false);
  
  const [deliverablesReloadKey, setDeliverablesReloadKey] = useState(0);
  
  const toast = useToast();

  useEffect(() => {
    setDescription(task?.description || '');
  }, [task?.description]);

  const handleSaveDescription = async () => {
    if (!task?.id) {
      toast({
        title: "Error",
        description: "Task ID not found",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true);
    try {
      await updateCardDescription(task.id, description);
      toast({
        title: "Success",
        description: "Description updated successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setIsEditingDescription(false);
    } catch (error) {
      console.error("Error updating description:", error);
      toast({
        title: "Error",
        description: error?.response?.data?.message || "Failed to update description",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setDescription(task?.description || '');
    setIsEditingDescription(false);
  };

  const handleDeliverableMarked = useCallback(() => {
    setDeliverablesReloadKey((prevKey) => prevKey + 1);
  }, []);

  const tabsData = [
    { label: "Designs", component: RelatedDesigns, props: { onDeliverableMarked: handleDeliverableMarked } },
    { label: "Deliverables", component: DeliveryTab, props: { reloadKey: deliverablesReloadKey } },
    { label: "Comments", component: CommentsTab, props: {} },
    { label: "Project Comments", component: ProjectCommentsTab, props: {} },
    { label: "Activity Logs", component: HistoryTab, props: {} },
    { label: "Attachments", component: AttachmentsTab, props: {} },
    { label: "Work Log", component: WorkLog, props: {} },
  ];

  return (
    <VStack align="stretch" spacing={6}>
      <Labels task={task} onUpdate={(updated) => console.log("Updated labels:", updated)} />

      <Box>
        <HStack justify="space-between" align="center" mb={3}>
          <Text fontSize="14px" fontWeight="600" color="#172B4D">
            Description
          </Text>
          {!isEditingDescription && (
            <IconButton
              icon={<EditIcon />}
              size="sm"
              variant="ghost"
              colorScheme="blue"
              aria-label="Edit description"
              onClick={() => setIsEditingDescription(true)}
            />
          )}
        </HStack>

        {isEditingDescription ? (
          <Box>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description..."
              fontSize="14px"
              color="#172B4D"
              lineHeight="1.5"
              minH="100px"
              resize="vertical"
              borderColor="#DFE1E6"
              _hover={{ borderColor: "#B3BAC5" }}
              _focus={{ borderColor: "#0052CC", boxShadow: "0 0 0 1px #0052CC" }}
            />
            <HStack mt={2} spacing={2}>
              <Button
                size="sm"
                colorScheme="blue"
                onClick={handleSaveDescription}
                isLoading={isSaving}
                loadingText="Saving..."
              >
                Save
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleCancelEdit}
                isDisabled={isSaving}
              >
                Cancel
              </Button>
            </HStack>
          </Box>
        ) : (
          <Box
            p={3}
            bg="#F4F5F7"
            borderRadius="md"
            minH="60px"
            cursor="pointer"
            _hover={{ bg: "#EBECF0" }}
            onClick={() => setIsEditingDescription(true)}
          >
            <Text fontSize="14px" color="#172B4D" lineHeight="1.5">
              {description || 'Click to add description...'}
            </Text>
          </Box>
        )}
      </Box>

      <Box>
        <Tabs variant="line" colorScheme="blue">
          <ScrollableTabList>
            {tabsData.map((tab, index) => (
              <Tab
                key={index}
                fontSize="14px"
                fontWeight="500"
                color="#6B778C"
                px={4}
                mr={4}
                whiteSpace="nowrap"
                flexShrink={0}
                _selected={{
                  color: "#0052CC",
                  borderColor: "#0052CC",
                }}
                _hover={{
                  color: "#172B4D",
                }}
              >
                {tab.label}
              </Tab>
            ))}
          </ScrollableTabList>
          
          <TabPanels>
            {tabsData.map((tab, index) => {
              const Component = tab.component;
              return (
                <TabPanel key={index} px={0} py={4}>
                  <Component task={task} {...tab.props} />
                </TabPanel>
              );
            })}
          </TabPanels>
        </Tabs>
      </Box>
    </VStack>
  );
};

export default ModalLeftSection;
